import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as candidatesAPI from '../../utils/api/candidates.js';
import { getErrorMessage } from '../../utils/api/errors.js';
import { normalizePagination } from '../../utils/pagination.js';

const rejectMsg = (error, fallback) =>
  getErrorMessage(error, fallback);

const listItemFromFull = (full) => ({
  id: full.id,
  name: full.name || full.fullName,
  email: full.email,
  phone: full.phone || '',
  experienceLevel: full.experienceLevel || full.experience || '',
  yearsOfExperience: full.yearsOfExperience || '',
  experience: full.experienceLevel || full.experience || 'Not specified',
  location: full.preferredJobLocation || full.location || '',
  skills: full.skills || [],
  submissionSource: full.submissionSource || 'manual',
  reviewStatus: full.reviewStatus || 'approved'
});

export const fetchCandidates = createAsyncThunk(
  'candidates/fetchCandidates',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await candidatesAPI.getCandidates(params);
      return response || { data: [], pagination: {} };
    } catch (error) {
      return rejectWithValue(rejectMsg(error, 'Failed to fetch candidates'));
    }
  }
);

export const fetchCandidateById = createAsyncThunk(
  'candidates/fetchCandidateById',
  async (id, { rejectWithValue }) => {
    try {
      return await candidatesAPI.getCandidateById(id);
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch candidate'
      );
    }
  }
);

export const createCandidate = createAsyncThunk(
  'candidates/createCandidate',
  async (arg, { rejectWithValue }) => {
    try {
      const payload = arg?.payload ?? arg;
      const resumeFile = arg?.resumeFile ?? null;
      const created = await candidatesAPI.createCandidate(payload, resumeFile);
      return created;
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to create candidate'
      );
    }
  }
);

export const updateCandidate = createAsyncThunk(
  'candidates/updateCandidate',
  async ({ id, payload, resumeFile = null }, { rejectWithValue }) => {
    try {
      return await candidatesAPI.updateCandidate(id, payload, resumeFile);
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to update candidate'
      );
    }
  }
);

export const deleteCandidate = createAsyncThunk(
  'candidates/deleteCandidate',
  async (id, { rejectWithValue }) => {
    try {
      await candidatesAPI.deleteCandidate(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to delete candidate'
      );
    }
  }
);

export const deleteCandidatesBulkThunk = createAsyncThunk(
  'candidates/deleteBulk',
  async (ids, { rejectWithValue }) => {
    const list = Array.isArray(ids)
      ? [...new Set(ids.map((id) => parseInt(id, 10)).filter((id) => Number.isFinite(id) && id > 0))]
      : [];
    if (list.length === 0) {
      return rejectWithValue('No candidates selected');
    }
    try {
      const result = await candidatesAPI.bulkDeleteCandidates(list);
      return {
        ids: list,
        deleted: Number(result?.deleted ?? list.length),
      };
    } catch (error) {
      return rejectWithValue(rejectMsg(error, 'Failed to delete candidates'));
    }
  }
);

export const updateCandidateReviewStatusThunk = createAsyncThunk(
  'candidates/updateCandidateReviewStatus',
  async ({ id, reviewStatus }, { rejectWithValue }) => {
    try {
      return await candidatesAPI.updateCandidateReviewStatus(id, reviewStatus);
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to update candidate review status'
      );
    }
  }
);

/**
 * @param {{ sheetRow: number, payload: object }[]} rows
 * @param {{ limit?: number, search?: string }} refreshParams
 */
const importCandidateRows = async (rows, refreshParams) => {
  const failures = [];
  let created = 0;
  for (const { sheetRow, payload } of rows) {
    try {
      await candidatesAPI.createCandidate(payload);
      created += 1;
    } catch (error) {
      failures.push({
        sheetRow,
        message:
          error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to create candidate'
      });
    }
  }
  let refresh = null;
  if (created > 0) {
    try {
      refresh = await candidatesAPI.getCandidates(
        refreshParams || { limit: 100, search: '' }
      );
    } catch {
      /* list refresh optional */
    }
  }
  return { created, failures, attempted: rows.length, refresh };
};

/** Bulk create from Excel-parsed rows (sequential API calls, then optional list refresh). */
export const createCandidatesBulkThunk = createAsyncThunk(
  'candidates/createBulk',
  async (input, { rejectWithValue }) => {
    const rows = Array.isArray(input) ? input : input?.rows;
    const refreshParams = Array.isArray(input)
      ? { limit: 100, search: '' }
      : input?.refreshParams ?? { limit: 100, search: '' };
    if (!Array.isArray(rows) || rows.length === 0) {
      return rejectWithValue('No candidates to import');
    }
    return importCandidateRows(rows, refreshParams);
  }
);

const initialState = {
  items: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  loading: false,
  error: null,
  /** Full profile for the candidate detail screen (and kept in sync when fetching by id elsewhere). */
  currentCandidate: null,
  detailLoading: false,
  detailError: null
};

const candidatesSlice = createSlice({
  name: 'candidates',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCandidate: (state) => {
      state.currentCandidate = null;
      state.detailError = null;
      state.detailLoading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload instanceof Error) {
          state.error = getErrorMessage(action.payload, 'Failed to fetch candidates');
          return;
        }
        state.items = action.payload?.data || [];
        state.pagination = normalizePagination(action.payload?.pagination || {}, {
          ...state.pagination,
          total: state.items.length,
        });
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCandidateById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchCandidateById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentCandidate = action.payload;
        state.detailError = null;
      })
      .addCase(fetchCandidateById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        state.currentCandidate = null;
      })
      .addCase(createCandidate.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCandidate.fulfilled, (state, action) => {
        if (action.payload?.id) {
          const row = listItemFromFull(action.payload);
          const idx = state.items.findIndex((c) => c.id === row.id);
          if (idx !== -1) state.items[idx] = row;
          if (state.currentCandidate?.id === action.payload.id) {
            state.currentCandidate = action.payload;
          }
        }
      })
      .addCase(updateCandidate.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c.id !== action.payload);
        state.pagination.total = Math.max(0, (state.pagination.total || 1) - 1);
        if (state.currentCandidate?.id === action.payload) {
          state.currentCandidate = null;
        }
      })
      .addCase(deleteCandidate.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteCandidatesBulkThunk.fulfilled, (state, action) => {
        const removed = new Set(action.payload?.ids || []);
        state.items = state.items.filter((item) => !removed.has(item.id));
        const deletedCount = Number(action.payload?.deleted || removed.size);
        if (deletedCount > 0) {
          state.pagination.total = Math.max(0, Number(state.pagination.total || 0) - deletedCount);
        }
        if (state.currentCandidate && removed.has(state.currentCandidate.id)) {
          state.currentCandidate = null;
        }
      })
      .addCase(deleteCandidatesBulkThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCandidateReviewStatusThunk.fulfilled, (state, action) => {
        if (action.payload?.id) {
          const row = listItemFromFull(action.payload);
          const idx = state.items.findIndex((c) => c.id === row.id);
          if (idx !== -1) state.items[idx] = row;
          if (state.currentCandidate?.id === action.payload.id) {
            state.currentCandidate = action.payload;
          }
        }
      })
      .addCase(updateCandidateReviewStatusThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createCandidatesBulkThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCandidatesBulkThunk.fulfilled, (state, action) => {
        state.loading = false;
        const r = action.payload?.refresh;
        if (r) {
          state.items = r?.data || [];
          state.pagination = normalizePagination(r?.pagination || {}, {
            ...state.pagination,
            limit: 100,
            total: state.items.length,
          });
        }
      })
      .addCase(createCandidatesBulkThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearCurrentCandidate } = candidatesSlice.actions;
export default candidatesSlice.reducer;
